import type {
  ArmLayout,
  DatasetAnalyticsCameraStreamCount,
  DatasetAnalyticsNumericSummary,
  DatasetAnalyticsOperatorCount,
  DatasetAnalyticsTaskEmbodimentCell,
  DatasetAnalyticsTrendBucket,
  DatasetAnalyticsTrendPoint,
  DatasetAnalyticsValueCount,
} from "@/api";
import { formatDataOrigin } from "@/components/dataset/dataOrigin";
import { formatArmLayout, formatRecordingMode } from "@/components/filter/datasetFilters";
import { labelValueText } from "@/components/label";
import { formatDuration, parseUtcMs } from "@/utils/datetime";
import { shareSlotColor, UNKNOWN_FILL } from "./charts";
import type {
  CategoryDatum,
  HeatmapAxisItem,
  HeatmapDatum,
  QuantileSummary,
  ShareSegment,
  TrendDatum,
} from "./charts";

/**
 * 통계 응답을 차트 계약(`charts/contract`)으로 바꾸는 순수 층.
 *
 * 접기, 정렬, 비율, 문구는 모두 여기서 처리한다. 차트는 받은 순서대로 그리기만 한다. 계산이
 * 렌더링과 섞이지 않아야 차트 구현을 교체해도 이 층의 테스트가 그대로 남는다.
 */

/** 가로 막대 카드가 한 번에 보여 주는 값 수. 넘치는 나머지는 `기타` 한 줄로 접는다. */
export const TOP_VALUE_LIMIT = 8;

// 버킷 하나의 길이(ms). `bucket_start` 가 UTC 자정 기준이라 ms 계산이 정확하다. 로컬 시간대나
// DST 가 영향을 주지 않는다.
const BUCKET_STRIDE_MS: Readonly<Record<DatasetAnalyticsTrendBucket, number>> = {
  day: 86_400_000,
  week: 604_800_000,
};

/** 미판정·미지정 묶음의 문구. 축마다 비어 있는 대상이 달라서 한 단어로 합치지 않는다. */
export type UnknownAxis = "operator" | "tip" | "cameraStream" | "default";

export function unknownLabel(axis: UnknownAxis): string {
  switch (axis) {
    case "operator":
      return "담당자 미지정";
    case "tip":
      return "팁 미지정";
    case "cameraStream":
      return "미산출";
    default:
      return "미판정";
  }
}

/**
 * 값별 개수를 카테고리 계약으로 바꾼다.
 *
 * 서버가 이미 개수 내림차순으로 주므로 순서를 바꾸지 않는다. 미판정 묶음만 맨 뒤로 옮긴다.
 * 개수가 많다고 "값이 없음" 이 축의 맨 앞에 오면 사용자가 축을 잘못 읽는다.
 */
export function toCategoryData(
  counts: readonly DatasetAnalyticsValueCount[],
  options: { formatValue?: (value: string) => string; unknownAxis?: UnknownAxis } = {},
): CategoryDatum[] {
  const { formatValue = (value: string) => value, unknownAxis = "default" } = options;
  const known: CategoryDatum[] = [];
  const unknown: CategoryDatum[] = [];
  for (const item of counts) {
    if (item.value === null) {
      unknown.push({
        key: "__unknown",
        label: unknownLabel(unknownAxis),
        count: item.count,
        unknown: true,
      });
    } else {
      known.push({ key: item.value, label: formatValue(item.value), count: item.count });
    }
  }
  return [...known, ...unknown];
}

/** 담당자 축. 표시명이 비어 있으면 uuid 대신 안내 문구를 쓴다. */
export function toOperatorData(counts: readonly DatasetAnalyticsOperatorCount[]): CategoryDatum[] {
  const known: CategoryDatum[] = [];
  const unknown: CategoryDatum[] = [];
  for (const item of counts) {
    if (item.member === null) {
      unknown.push({
        key: "__unknown",
        label: unknownLabel("operator"),
        count: item.count,
        unknown: true,
      });
    } else {
      known.push({
        key: item.member.uuid,
        label: item.member.display_name ?? "이름 없는 멤버",
        count: item.count,
      });
    }
  }
  return [...known, ...unknown];
}

/**
 * 카메라 스트림 수 축. 순서형이라 **값 오름차순**으로 둔다(개수 순이 아니다).
 * 미산출 묶음은 순서가 없으므로 맨 끝에 따로 붙인다.
 */
export function toCameraStreamData(
  counts: readonly DatasetAnalyticsCameraStreamCount[],
): CategoryDatum[] {
  const known = counts
    .filter(
      (item): item is DatasetAnalyticsCameraStreamCount & { value: number } => item.value !== null,
    )
    .sort((left, right) => left.value - right.value)
    .map((item) => ({ key: String(item.value), label: String(item.value), count: item.count }));
  const unknown = counts
    .filter((item) => item.value === null)
    .map((item) => ({
      key: "__unknown",
      label: unknownLabel("cameraStream"),
      count: item.count,
      unknown: true,
    }));
  return [...known, ...unknown];
}

/**
 * 상위 `limit` 개만 남기고 나머지를 `기타` 한 줄로 접는다.
 *
 * 색을 더 만들어 항목을 늘리지 않기 위한 장치다. 접힌 개수는 호출부가 카드 부제에 적는다.
 */
export function foldTail(data: readonly CategoryDatum[], limit = TOP_VALUE_LIMIT): CategoryDatum[] {
  if (data.length <= limit) {
    return [...data];
  }
  const head = data.slice(0, limit);
  const tail = data.slice(limit);
  const tailCount = tail.reduce((sum, item) => sum + item.count, 0);
  return [
    ...head,
    { key: "__other", label: `기타 ${tail.length}종`, count: tailCount, unknown: true },
  ];
}

/** `foldTail` 이 접을 값의 수. 0 이면 접지 않는다. */
export function foldedCount(data: readonly CategoryDatum[], limit = TOP_VALUE_LIMIT): number {
  return Math.max(0, data.length - limit);
}

/**
 * 부분-전체 조각으로 바꾼다.
 *
 * 슬롯은 **값의 고정 vocab 순서**(`vocabOrder`)로 정한다. 서버 배열은 개수 순이라 인덱스로
 * 색을 정하면 데이터가 바뀔 때 같은 값의 색이 바뀐다. vocab 밖 값은 순서 뒤에 추가한다. 그래서
 * 계약에 새 값이 먼저 들어와도 화면에서 사라지지 않는다.
 */
export function toShareSegments(
  counts: readonly DatasetAnalyticsValueCount[],
  vocabOrder: readonly string[],
  options: { formatValue?: (value: string) => string; unknownAxis?: UnknownAxis } = {},
): ShareSegment[] {
  const { formatValue = (value: string) => value, unknownAxis = "default" } = options;
  const total = counts.reduce((sum, item) => sum + item.count, 0);
  if (total === 0) {
    return [];
  }
  const extras = counts
    .map((item) => item.value)
    .filter((value): value is string => value !== null && !vocabOrder.includes(value));
  const order = [...vocabOrder, ...extras];
  const segments: ShareSegment[] = [];
  for (const [slot, value] of order.entries()) {
    const found = counts.find((item) => item.value === value);
    if (found === undefined || found.count === 0) {
      continue;
    }
    segments.push({
      key: value,
      label: formatValue(value),
      count: found.count,
      share: found.count / total,
      color: shareSlotColor(slot),
    });
  }
  const unknown = counts.find((item) => item.value === null);
  if (unknown !== undefined && unknown.count > 0) {
    segments.push({
      key: "__unknown",
      label: unknownLabel(unknownAxis),
      count: unknown.count,
      share: unknown.count / total,
      color: UNKNOWN_FILL,
      unknown: true,
    });
  }
  return segments;
}

/** 추이 카드가 전환하는 지표. trend point 의 어느 필드를 시리즈로 쓸지 정한다. */
export type TrendMetric = "count" | "episodes" | "duration" | "size";

const TREND_METRIC_VALUE: Readonly<
  Record<TrendMetric, (point: DatasetAnalyticsTrendPoint) => number>
> = {
  count: (point) => point.count,
  episodes: (point) => point.episode_count,
  duration: (point) => point.total_duration_sec,
  size: (point) => point.total_size_bytes,
};

/**
 * 추이 응답에서 선택한 지표의 시리즈를 만들고 빈 버킷을 0 으로 채운다.
 *
 * 서버는 값이 있는 버킷만 보낸다. 그대로 이으면 아무것도 없던 구간이 직선으로 연결되어
 * "그동안 꾸준히 모았다" 로 잘못 읽힌다. 볼륨 지표도 같은 규칙이다. 버킷에 데이터셋이 없으면
 * 볼륨도 0 이다.
 */
export function toTrendSeries(
  points: readonly DatasetAnalyticsTrendPoint[],
  bucket: DatasetAnalyticsTrendBucket,
  metric: TrendMetric,
): TrendDatum[] {
  const valueOf = TREND_METRIC_VALUE[metric];
  const parsed: TrendDatum[] = [];
  for (const point of points) {
    const atMs = parseUtcMs(point.bucket_start);
    if (atMs !== null) {
      parsed.push({ atMs, value: valueOf(point) });
    }
  }
  if (parsed.length === 0) {
    return [];
  }
  parsed.sort((left, right) => left.atMs - right.atMs);
  const stride = BUCKET_STRIDE_MS[bucket];
  const valueByBucket = new Map(parsed.map((point) => [point.atMs, point.value]));
  const filled: TrendDatum[] = [];
  const last = parsed[parsed.length - 1].atMs;
  for (let atMs = parsed[0].atMs; atMs <= last; atMs += stride) {
    filled.push({ atMs, value: valueByBucket.get(atMs) ?? 0 });
  }
  return filled;
}

/**
 * BE `_bucket_start_of` 와 같은 규칙의 FE 구현. 시각이 속한 버킷의 시작(ms)을 반환한다.
 *
 * week 는 ISO 규약(월요일 시작)이다. 1970-01-01 이 목요일이라 일수에 3 을 더하면 월요일
 * 기준 나머지가 나온다. UTC ms 계산이라 로컬 시간대·DST 와 관계없다.
 */
export function bucketStartMsOf(atMs: number, bucket: DatasetAnalyticsTrendBucket): number {
  const dayMs = BUCKET_STRIDE_MS.day;
  const dayIndex = Math.floor(atMs / dayMs);
  if (bucket === "day") {
    return dayIndex * dayMs;
  }
  const daysSinceMonday = (dayIndex + 3) % 7;
  return (dayIndex - daysSinceMonday) * dayMs;
}

/** 추이 요약. 이번(벽시계) 버킷 값, 직전 버킷 값, 최근 평균. 뷰가 문구와 포맷을 정한다. */
export type TrendSummary = {
  current: number;
  previous: number;
  recentAverage: number;
  /** 평균에 실제로 들어간 버킷 수. 문구("최근 N주 평균")가 이 수를 쓴다. */
  recentSpan: number;
};

/** 최근 평균에 넣는 최대 버킷 수(이번 버킷 포함). */
const RECENT_BUCKET_SPAN = 4;

/**
 * 수집 추이의 요약을 만든다.
 *
 * - "이번 버킷" 은 시리즈의 마지막이 아니라 `nowMs` 가 속한 벽시계 버킷이다. 그래서 최근
 *   수집이 멈췄을 때 과거 값을 "이번" 으로 읽지 않는다. 시리즈에 없는 버킷은 0 으로 본다.
 * - 평균은 시리즈 시작 이전 버킷을 포함하지 않는다. 기간이 4버킷보다 짧으면 있는 버킷 수로
 *   나눈다.
 * - 빈 시리즈면 null 이다. 카드는 요약 줄을 그리지 않는다.
 */
export function summarizeTrend(
  trend: readonly TrendDatum[],
  bucket: DatasetAnalyticsTrendBucket,
  nowMs: number,
): TrendSummary | null {
  if (trend.length === 0) {
    return null;
  }
  const stride = BUCKET_STRIDE_MS[bucket];
  const currentStart = bucketStartMsOf(nowMs, bucket);
  const valueByBucket = new Map(trend.map((point) => [point.atMs, point.value]));
  const valueAt = (startMs: number): number => valueByBucket.get(startMs) ?? 0;
  const bucketsSinceFirst = Math.floor((currentStart - trend[0].atMs) / stride) + 1;
  const recentSpan = Math.min(RECENT_BUCKET_SPAN, Math.max(1, bucketsSinceFirst));
  let recentTotal = 0;
  for (let index = 0; index < recentSpan; index += 1) {
    recentTotal += valueAt(currentStart - stride * index);
  }
  return {
    current: valueAt(currentStart),
    previous: valueAt(currentStart - stride),
    recentAverage: recentTotal / recentSpan,
    recentSpan,
  };
}

/** 분위 요약을 차트 계약으로 바꾼다. 표본이 하나도 없으면(모든 필드 null) `null` 이다. */
export function toQuantileSummary(summary: DatasetAnalyticsNumericSummary): QuantileSummary | null {
  const { min, p25, p50, p75, max } = summary;
  if (min === null || p25 === null || p50 === null || p75 === null || max === null) {
    return null;
  }
  return { min, p25, p50, p75, max };
}

/** 히트맵 화면 모델. 행, 열, 셀, 접힌 task 수(카드 부제가 적는다). */
export type HeatmapModel = {
  rows: HeatmapAxisItem[];
  columns: HeatmapAxisItem[];
  data: HeatmapDatum[];
  foldedTaskCount: number;
};

/**
 * task × embodiment 교차 응답을 히트맵 계약으로 바꾼다.
 *
 * - 행(task)은 합계 내림차순 상위 `taskLimit` 개만 남긴다. 나머지는 "기타" 한 행에 열별로
 *   합산한다. 막대 카드의 접기 규약과 같다.
 * - 열(embodiment)은 나온 값을 모두 합계 내림차순으로 둔다(열이 많으면 카드가 가로 스크롤한다).
 * - intensity 는 접기 후 최대 셀을 기준으로 0..1 정규화한다.
 * - 셀이 하나도 없으면 null 이다. 카드가 빈 문구를 그린다.
 */
export function toTaskEmbodimentHeatmap(
  cells: readonly DatasetAnalyticsTaskEmbodimentCell[],
  options: { taskLimit?: number } = {},
): HeatmapModel | null {
  const { taskLimit = TOP_VALUE_LIMIT } = options;
  const drawn = cells.filter((cell) => cell.count > 0);
  if (drawn.length === 0) {
    return null;
  }
  // 축 합계. 정렬과 접기의 근거다. 서버 정렬은 셀 단위라서 행·열 합계는 여기서 다시 센다.
  const taskTotals = new Map<string, number>();
  const embodimentTotals = new Map<string, number>();
  for (const cell of drawn) {
    taskTotals.set(cell.task, (taskTotals.get(cell.task) ?? 0) + cell.count);
    embodimentTotals.set(
      cell.embodiment,
      (embodimentTotals.get(cell.embodiment) ?? 0) + cell.count,
    );
  }
  const byTotalDesc = (left: [string, number], right: [string, number]): number =>
    right[1] - left[1] || left[0].localeCompare(right[0]);
  const sortedTasks = [...taskTotals.entries()].sort(byTotalDesc).map(([task]) => task);
  const foldedTasks = new Set(sortedTasks.slice(taskLimit));

  const taskText = labelAxisText("task");
  const rows: HeatmapAxisItem[] = sortedTasks
    .slice(0, taskLimit)
    .map((task) => ({ key: task, label: taskText(task) }));
  if (foldedTasks.size > 0) {
    rows.push({ key: "__other", label: `기타 ${foldedTasks.size}종`, unknown: true });
  }
  const columns: HeatmapAxisItem[] = [...embodimentTotals.entries()]
    .sort(byTotalDesc)
    .map(([name]) => ({ key: name, label: name }));

  // 접힌 task 들의 셀은 열별로 "기타" 행에 합친다.
  const matrix = new Map<string, Map<string, number>>();
  for (const cell of drawn) {
    const rowKey = foldedTasks.has(cell.task) ? "__other" : cell.task;
    const row = matrix.get(rowKey) ?? new Map<string, number>();
    row.set(cell.embodiment, (row.get(cell.embodiment) ?? 0) + cell.count);
    matrix.set(rowKey, row);
  }
  let maxCount = 0;
  for (const row of matrix.values()) {
    for (const count of row.values()) {
      maxCount = Math.max(maxCount, count);
    }
  }
  const data: HeatmapDatum[] = [];
  for (const [rowKey, row] of matrix) {
    for (const [columnKey, count] of row) {
      data.push({ rowKey, columnKey, count, intensity: count / maxCount });
    }
  }
  return { rows, columns, data, foldedTaskCount: foldedTasks.size };
}

/**
 * 총 길이(초) 표기. 목록 카드(최근 등록, 분량 상위)가 함께 쓴다.
 *
 * 표·카드의 길이 컬럼과 같은 `formatDuration`(ms) 규칙이다. null 은 0 이 아니라 미측정이다
 * (summary 미생성, 또는 ZERG-158 길이 backfill 전). 그래서 대시로 그린다.
 */
export function totalDurationText(seconds: number | null): string {
  return formatDuration(seconds === null ? null : seconds * 1000);
}

/** 라벨 축(task/environment/source) 값의 표시 문구. 칩·필터와 같은 어휘를 쓴다. */
export function labelAxisText(key: string): (value: string) => string {
  return (value: string) => labelValueText(key, value);
}

/** 수집 방식 문구. 통계 축은 데이터셋 단위 컬럼이라 롤아웃 접기를 하지 않는다. */
export function recordingModeText(value: string): string {
  return formatRecordingMode(value) ?? value;
}

/** 팔 구성 문구. vocab 밖 값이면 `formatArmLayout` 이 값을 그대로 반환한다. */
export function armLayoutText(value: string): string {
  return formatArmLayout(value as ArmLayout) ?? value;
}

/** 행동 주체 문구. */
export function dataOriginText(value: string): string {
  return formatDataOrigin(value) ?? value;
}
